import { downloadTableAsCSV } from "../utils/csvUtils";
import {
  TableChart,
  Visibility,
  Restore,
  DeleteSweep,
  ZoomIn,
  ZoomOut,
  FilterAlt,
  Download,
} from "@mui/icons-material";
import {
  Box,
  IconButton,
  Typography,
  Paper,
  Menu,
  MenuItem,
  ListItemText,
  Divider,
  Badge,
  TextField,
} from "@mui/material";
import React, { useState, memo } from "react";

/**
 * ExcelGridNavigationBar component for table-specific controls
 * Contains zoom, show stats, show confidence, and recovery functionality
 */
const ExcelGridNavigationBar = ({
  // Toggle states
  showStats,
  setShowStats,
  showConfidence,
  setShowConfidence,

  // Zoom controls
  tableZoom,
  setTableZoom,

  // Confidence filtering
  confidenceFilterEnabled,
  setConfidenceFilterEnabled,
  confidenceThreshold,
  setConfidenceThreshold,

  // Recovery data and functions
  deletedRows,
  deletedColumns,
  recoverRowById,
  recoverColumnById,
  recoverAllRows,
  recoverAllColumns,

  // CSV download
  tableData,
  selectedTable,
}) => {
  const [recoveryMenuAnchor, setRecoveryMenuAnchor] = useState(null);

  // Download CSV with confidence filtering
  const handleDownloadCSV = () => {
    downloadTableAsCSV(
      tableData,
      selectedTable,
      confidenceThreshold,
      confidenceFilterEnabled
    );
  };

  // Helper to get original index from ID
  const getOriginalIndexFromId = (id, type) => {
    const parts = id.split("_");
    return parts.length >= 2 && parts[0] === type ? parseInt(parts[1], 10) : -1;
  };

  // Recovery handlers
  const handleRecoverRow = (rowId) => {
    recoverRowById(rowId);
    setRecoveryMenuAnchor(null);
  };

  const handleRecoverColumn = (columnId) => {
    recoverColumnById(columnId);
    setRecoveryMenuAnchor(null);
  };

  const handleRecoverAllRows = () => {
    if (recoverAllRows) {
      recoverAllRows();
    }
    setRecoveryMenuAnchor(null);
  };

  const handleRecoverAllColumns = () => {
    if (recoverAllColumns) {
      recoverAllColumns();
    }
    setRecoveryMenuAnchor(null);
  };

  const totalDeletedItems =
    (deletedRows?.size || 0) + (deletedColumns?.size || 0);

  return (
    <Paper
      elevation={1}
      sx={{
        px: { xs: 0.75, sm: 1 },
        py: { xs: 0.375, sm: 0.5 },
        mb: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: { xs: 0.5, sm: 0.75 },
        borderRadius: 1,
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        border: "1px solid rgba(0, 0, 0, 0.08)",
        alignSelf: "center",
        width: "fit-content",
        mx: "auto",
        maxWidth: "calc(100% - 32px)",
        overflowX: "auto",
        overflowY: "hidden",
        "&::-webkit-scrollbar": {
          height: "4px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "rgba(0, 0, 0, 0.05)",
          borderRadius: "2px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "rgba(0, 0, 0, 0.2)",
          borderRadius: "2px",
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.3)",
          },
        },
      }}
    >
      {/* Table Zoom Controls*/}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 0.25, sm: 0.5 },
          px: { xs: 0.5, sm: 0.75 },
          py: 0.25,
          borderRadius: 0.5,
        }}
      >
        <IconButton
          onClick={() => setTableZoom(Math.max(0.5, tableZoom - 0.1))}
          size={"small"}
          sx={{ p: 0.375 }}
          title={"Zoom Out Table"}
        >
          <ZoomOut fontSize={"small"} />
        </IconButton>

        <Typography
          variant={"body2"}
          sx={{
            fontSize: { xs: "0.65rem", sm: "0.7rem" },
            fontWeight: 500,
            color: "text.secondary",
            minWidth: { xs: "40px", sm: "45px" },
            textAlign: "center",
            whiteSpace: "nowrap",
          }}
        >
          {`${Math.round(tableZoom * 100)}%`}
        </Typography>

        <IconButton
          onClick={() => setTableZoom(Math.min(2.0, tableZoom + 0.1))}
          size={"small"}
          sx={{ p: 0.375 }}
          title={"Zoom In Table"}
        >
          <ZoomIn fontSize={"small"} />
        </IconButton>
      </Box>

      {/* Divider */}
      <Box
        sx={{
          width: "1px",
          height: "20px",
          backgroundColor: "rgba(0, 0, 0, 0.1)",
        }}
      />

      {/* Show Stats Toggle */}
      <Box
        onClick={() => setShowStats(!showStats)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 0.5, sm: 0.75 },
          cursor: "pointer",
          px: { xs: 0.5, sm: 0.75 },
          py: 0.25,
          borderRadius: 0.5,
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          },
        }}
      >
        <IconButton
          size={"small"}
          color={showStats ? "primary" : "default"}
          sx={{ p: 0.375, pointerEvents: "none" }}
        >
          <TableChart fontSize={"small"} />
        </IconButton>
        <Typography
          variant={"body2"}
          sx={{
            fontSize: { xs: "0.7rem", sm: "0.75rem" },
            fontWeight: 500,
            color: showStats ? "primary.main" : "text.secondary",
            whiteSpace: "nowrap",
          }}
        >
          {"Show Stats"}
        </Typography>
      </Box>

      {/* Divider */}
      <Box
        sx={{
          width: "1px",
          height: "20px",
          backgroundColor: "rgba(0, 0, 0, 0.1)",
        }}
      />

      {/* Show Confidence Toggle */}
      <Box
        onClick={() => setShowConfidence(!showConfidence)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 0.5, sm: 0.75 },
          cursor: "pointer",
          px: { xs: 0.5, sm: 0.75 },
          py: 0.25,
          borderRadius: 0.5,
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          },
        }}
      >
        <IconButton
          size={"small"}
          color={showConfidence ? "primary" : "default"}
          sx={{ p: 0.375, pointerEvents: "none" }}
        >
          <Visibility fontSize={"small"} />
        </IconButton>
        <Typography
          variant={"body2"}
          sx={{
            fontSize: { xs: "0.7rem", sm: "0.75rem" },
            fontWeight: 500,
            color: showConfidence ? "primary.main" : "text.secondary",
            whiteSpace: "nowrap",
          }}
        >
          {"Show Confidence Score"}
        </Typography>
      </Box>

      {/* Divider */}
      <Box
        sx={{
          width: "1px",
          height: "20px",
          backgroundColor: "rgba(0, 0, 0, 0.1)",
        }}
      />

      {/* Filter Low Confidence Score */}
      <Box
        onClick={() =>
          !confidenceFilterEnabled && setConfidenceFilterEnabled(true)
        }
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 0.5, sm: 0.75 },
          cursor: !confidenceFilterEnabled ? "pointer" : "default",
          px: { xs: 0.5, sm: 0.75 },
          py: 0.25,
          borderRadius: 0.5,
          "&:hover": {
            backgroundColor: !confidenceFilterEnabled
              ? "rgba(0, 0, 0, 0.04)"
              : "transparent",
          },
        }}
      >
        <IconButton
          size={"small"}
          color={confidenceFilterEnabled ? "primary" : "default"}
          sx={{ p: 0.375, pointerEvents: "none" }}
        >
          <FilterAlt fontSize={"small"} />
        </IconButton>
        {confidenceFilterEnabled ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography
              variant={"body2"}
              sx={{
                fontSize: { xs: "0.7rem", sm: "0.75rem" },
                fontWeight: 500,
                color: "primary.main",
                whiteSpace: "nowrap",
              }}
            >
              {"<"}
            </Typography>
            <TextField
              value={Math.round(confidenceThreshold * 100)}
              onChange={(e) => {
                const value = parseInt(e.target.value) || 0;
                const clampedValue = Math.max(0, Math.min(100, value));
                setConfidenceThreshold(clampedValue / 100);
              }}
              onBlur={(e) => {
                if (e.target.value === "" || parseInt(e.target.value) === 0) {
                  setConfidenceFilterEnabled(false);
                }
              }}
              type={"number"}
              inputProps={{
                min: 0,
                max: 100,
                step: 1,
              }}
              sx={{
                width: "50px",
                "& .MuiInputBase-input": {
                  fontSize: { xs: "0.7rem", sm: "0.75rem" },
                  fontWeight: 500,
                  padding: "2px 4px",
                  textAlign: "center",
                  color: "primary.main",
                },
                "& .MuiOutlinedInput-root": {
                  height: "24px",
                  "& fieldset": {
                    borderColor: "primary.main",
                  },
                },
              }}
            />
            <Typography
              variant={"body2"}
              sx={{
                fontSize: { xs: "0.7rem", sm: "0.75rem" },
                fontWeight: 500,
                color: "primary.main",
                whiteSpace: "nowrap",
              }}
            >
              {"% Confidence"}
            </Typography>
            <IconButton
              size={"small"}
              onClick={(e) => {
                e.stopPropagation();
                setConfidenceFilterEnabled(false);
              }}
              sx={{ p: 0.25, ml: 0.5 }}
              title={"Clear filter"}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "12px",
                  fontWeight: "bold",
                  color: "text.secondary",
                }}
              >
                {"×"}
              </Box>
            </IconButton>
          </Box>
        ) : (
          <Typography
            variant={"body2"}
            sx={{
              fontSize: { xs: "0.7rem", sm: "0.75rem" },
              fontWeight: 500,
              color: "text.secondary",
              whiteSpace: "nowrap",
            }}
          >
            {"Filter Confidence Score"}
          </Typography>
        )}
      </Box>

      {/* Divider */}
      <Box
        sx={{
          width: "1px",
          height: "20px",
          backgroundColor: "rgba(0, 0, 0, 0.1)",
        }}
      />

      {/* Recovery Menu */}
      <Box
        onClick={(e) => setRecoveryMenuAnchor(e.currentTarget)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 0.5, sm: 0.75 },
          cursor: totalDeletedItems > 0 ? "pointer" : "not-allowed",
          px: { xs: 0.5, sm: 0.75 },
          py: 0.25,
          borderRadius: 0.5,
          opacity: totalDeletedItems > 0 ? 1 : 0.5,
          "&:hover": {
            backgroundColor:
              totalDeletedItems > 0 ? "rgba(0, 0, 0, 0.04)" : "transparent",
          },
        }}
      >
        <Badge
          badgeContent={totalDeletedItems}
          color={"error"}
          sx={{
            "& .MuiBadge-badge": {
              fontSize: "0.625rem",
              minWidth: "14px",
              height: "14px",
            },
          }}
        >
          <IconButton
            size={"small"}
            color={totalDeletedItems > 0 ? "primary" : "default"}
            sx={{ p: 0.375, pointerEvents: "none" }}
          >
            <Restore fontSize={"small"} />
          </IconButton>
        </Badge>
        <Typography
          variant={"body2"}
          sx={{
            fontSize: { xs: "0.7rem", sm: "0.75rem" },
            fontWeight: 500,
            color: totalDeletedItems > 0 ? "primary.main" : "text.secondary",
            whiteSpace: "nowrap",
          }}
        >
          {"Recovery"}
        </Typography>
      </Box>

      {/* Spacer to push CSV to the right */}
      <Box sx={{ flex: 1 }} />

      {/* Download CSV */}
      <Box
        onClick={handleDownloadCSV}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 0.25, sm: 0.375 },
          cursor: "pointer",
          px: { xs: 0.375, sm: 0.5 },
          py: 0.25,
          borderRadius: 0.5,
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          },
        }}
      >
        <IconButton
          size={"small"}
          sx={{ p: 0.25, pointerEvents: "none" }}
          title={"Download CSV"}
        >
          <Download fontSize={"small"} />
        </IconButton>
        <Typography
          variant={"body2"}
          sx={{
            fontSize: { xs: "0.65rem", sm: "0.7rem" },
            fontWeight: 500,
            color: "text.secondary",
            whiteSpace: "nowrap",
          }}
        >
          {"CSV"}
        </Typography>
      </Box>

      {/* Recovery Menu Dropdown */}
      <Menu
        open={Boolean(recoveryMenuAnchor)}
        anchorEl={recoveryMenuAnchor}
        onClose={() => setRecoveryMenuAnchor(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            minWidth: 220,
            maxHeight: 400,
            mt: 1,
          },
        }}
      >
        {/* Individual Rows Recovery */}
        {deletedRows && deletedRows.size > 0 && (
          <>
            <Typography
              variant={"subtitle2"}
              sx={{
                px: 2,
                py: 1,
                fontWeight: 600,
                color: "text.secondary",
                fontSize: "0.75rem",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              {"Deleted Rows"}
            </Typography>
            {Array.from(deletedRows.entries())
              .sort(
                ([, rowInfoA], [, rowInfoB]) =>
                  rowInfoA.originalIndex - rowInfoB.originalIndex
              )
              .map(([rowId, rowInfo]) => {
                const originalIndex = getOriginalIndexFromId(rowId, "row");
                const displayIndex =
                  originalIndex >= 0 ? originalIndex + 1 : "?";
                return (
                  <MenuItem
                    key={rowId}
                    onClick={() => handleRecoverRow(rowId)}
                    sx={{ py: 1 }}
                  >
                    <ListItemText
                      primary={`Row ${displayIndex}`}
                      secondary={rowInfo.content?.[0] || "Empty row"}
                      primaryTypographyProps={{ fontSize: "0.875rem" }}
                      secondaryTypographyProps={{
                        fontSize: "0.75rem",
                        noWrap: true,
                      }}
                    />
                  </MenuItem>
                );
              })}

            {/* Recover All Rows */}
            <MenuItem
              onClick={handleRecoverAllRows}
              disabled={deletedRows.size === 0}
              sx={{
                backgroundColor: "rgba(76, 175, 80, 0.08)",
                "&:hover": {
                  backgroundColor: "rgba(76, 175, 80, 0.12)",
                },
                borderTop: "1px solid rgba(0, 0, 0, 0.08)",
              }}
            >
              <DeleteSweep
                sx={{ mr: 1, fontSize: "1.1rem", color: "success.main" }}
              />
              <ListItemText primary={"Recover All Rows"} />
            </MenuItem>

            {deletedColumns && deletedColumns.size > 0 && <Divider />}
          </>
        )}

        {/* Individual Columns Recovery */}
        {deletedColumns && deletedColumns.size > 0 && (
          <>
            <Typography
              variant={"subtitle2"}
              sx={{
                px: 2,
                py: 1,
                fontWeight: 600,
                color: "text.secondary",
                fontSize: "0.75rem",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              {"Deleted Columns"}
            </Typography>
            {Array.from(deletedColumns.entries())
              .sort(
                ([, colInfoA], [, colInfoB]) =>
                  colInfoA.originalIndex - colInfoB.originalIndex
              )
              .map(([columnId, columnInfo]) => {
                const originalIndex = getOriginalIndexFromId(columnId, "col");
                const displayIndex =
                  originalIndex >= 0 ? originalIndex + 1 : "?";
                return (
                  <MenuItem
                    key={columnId}
                    onClick={() => handleRecoverColumn(columnId)}
                    sx={{ py: 1 }}
                  >
                    <ListItemText
                      primary={`Column ${displayIndex}`}
                      secondary={columnInfo.header || "No header"}
                      primaryTypographyProps={{ fontSize: "0.875rem" }}
                      secondaryTypographyProps={{
                        fontSize: "0.75rem",
                        noWrap: true,
                      }}
                    />
                  </MenuItem>
                );
              })}

            {/* Recover All Columns */}
            <MenuItem
              onClick={handleRecoverAllColumns}
              disabled={deletedColumns.size === 0}
              sx={{
                backgroundColor: "rgba(76, 175, 80, 0.08)",
                "&:hover": {
                  backgroundColor: "rgba(76, 175, 80, 0.12)",
                },
                borderTop: "1px solid rgba(0, 0, 0, 0.08)",
              }}
            >
              <DeleteSweep
                sx={{ mr: 1, fontSize: "1.1rem", color: "success.main" }}
              />
              <ListItemText primary={"Recover All Columns"} />
            </MenuItem>
          </>
        )}

        {/* No deleted items message */}
        {(!deletedRows || deletedRows.size === 0) &&
          (!deletedColumns || deletedColumns.size === 0) && (
            <MenuItem disabled>
              <ListItemText
                primary={"No deleted items"}
                secondary={"Delete rows or columns to see recovery options"}
              />
            </MenuItem>
          )}
      </Menu>
    </Paper>
  );
};

export default memo(ExcelGridNavigationBar);
